import React, { useState, useEffect, useMemo } from 'react';
import { Search, Loader2, AlertCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { fetchRolesWithRates } from '@/lib/data-service';
import type { RoleWithRates } from '@/lib/data-service';
import { ProjectRoleOverridesTab } from '@/components/RolesAndRates/ProjectRoleOverridesTab';
import { EmployeeRoleOverridesTab } from '@/components/RolesAndRates/EmployeeRoleOverridesTab';
import { useDataMode } from '@/contexts/DataModeContext';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export function RolesRatesPage() {
  const { mode } = useDataMode();
  const [roles, setRoles] = useState<RoleWithRates[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const loadRoles = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchRolesWithRates();
      setRoles(data);
    } catch (err) {
      console.error('Error loading roles:', err);
      setError('Failed to load roles. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, [mode]);

  const filteredRoles = useMemo(() => {
    if (!searchQuery.trim()) return roles;
    const lowerQuery = searchQuery.toLowerCase();
    return roles.filter(r =>
      r.name.toLowerCase().includes(lowerQuery) ||
      r.description?.toLowerCase().includes(lowerQuery)
    );
  }, [roles, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRoles = filteredRoles.slice(startIndex, endIndex);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Roles & Rates</h1>
      </div>

      <Tabs defaultValue="roles" className="space-y-4">
        <TabsList>
          <TabsTrigger value="roles">Roles & Rates</TabsTrigger>
          <TabsTrigger value="project-overrides">Project Overrides</TabsTrigger>
          <TabsTrigger value="employee-overrides">Employee Overrides</TabsTrigger>
        </TabsList>

        <TabsContent value="roles" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Roles & Rates</CardTitle>
                  <CardDescription>
                    View job roles and their billing and cost rates
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search roles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {isLoading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              )}

              {!isLoading && filteredRoles.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">
                    {searchQuery ? 'No roles match your search' : 'No roles found'}
                  </p>
                </div>
              )}

              {!isLoading && filteredRoles.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <Table className="text-sm" disableScroll>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="p-2 h-10 font-medium">Role</TableHead>
                        <TableHead className="p-2 h-10 font-medium">Description</TableHead>
                        <TableHead className="text-right p-2 h-10 font-medium">Cost Rate<br/>(Hour)</TableHead>
                        <TableHead className="text-right p-2 h-10 font-medium">Cost Rate<br/>(Day)</TableHead>
                        <TableHead className="text-right p-2 h-10 font-medium">Bill Rate<br/>(Hour)</TableHead>
                        <TableHead className="text-right p-2 h-10 font-medium">Bill Rate<br/>(Day)</TableHead>
                        <TableHead className="text-right p-2 h-10 font-medium">Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedRoles.map((role) => {
                        const costHourly = role.defaultCostRate || 0;
                        const costDaily = role.defaultDailyCostRate ?? costHourly * 8;
                        const chargeHourly = role.defaultBillRate || 0;
                        const chargeDaily = role.defaultDailyBillRate ?? chargeHourly * 8;
                        const margin = chargeHourly > 0 ? ((chargeHourly - costHourly) / chargeHourly * 100) : 0;

                        return (
                          <TableRow key={role.id}>
                            <TableCell className="p-2">
                              <div className="font-medium">{role.name}</div>
                            </TableCell>
                            <TableCell className="p-2">
                              <div className="text-muted-foreground truncate max-w-md">
                                {role.description || '\u2014'}
                              </div>
                            </TableCell>
                            <TableCell className="p-2 text-right">
                              {role.defaultCostRate ? (
                                <span className="font-medium">{formatCurrency(costHourly)}</span>
                              ) : (
                                <span className="text-muted-foreground">{'\u2014'}</span>
                              )}
                            </TableCell>
                            <TableCell className="p-2 text-right">
                              {role.defaultCostRate ? (
                                <span className="font-medium">{formatCurrency(costDaily)}</span>
                              ) : (
                                <span className="text-muted-foreground">{'\u2014'}</span>
                              )}
                            </TableCell>
                            <TableCell className="p-2 text-right">
                              {role.defaultBillRate ? (
                                <span className="font-medium text-success-600">{formatCurrency(chargeHourly)}</span>
                              ) : (
                                <span className="text-muted-foreground">{'\u2014'}</span>
                              )}
                            </TableCell>
                            <TableCell className="p-2 text-right">
                              {role.defaultBillRate ? (
                                <span className="font-medium text-success-600">{formatCurrency(chargeDaily)}</span>
                              ) : (
                                <span className="text-muted-foreground">{'\u2014'}</span>
                              )}
                            </TableCell>
                            <TableCell className="p-2 text-right">
                              {role.defaultBillRate && role.defaultCostRate ? (
                                <span className="font-medium text-blue-600">
                                  {margin.toFixed(0)}%
                                </span>
                              ) : (
                                <span className="text-muted-foreground">{'\u2014'}</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  {!isLoading && filteredRoles.length > 0 && (
                    <div className="flex items-center justify-between px-2 py-3 border-t">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>Showing {startIndex + 1} to {Math.min(endIndex, filteredRoles.length)} of {filteredRoles.length} results</span>
                        <span className="hidden sm:inline">•</span>
                        <div className="flex items-center gap-2">
                          <span className="hidden sm:inline">Per page:</span>
                          <Select
                            value={itemsPerPage.toString()}
                            onValueChange={(value) => {
                              setItemsPerPage(Number(value));
                              setCurrentPage(1);
                            }}
                          >
                            <SelectTrigger className="h-8 w-auto min-w-[60px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="10">10</SelectItem>
                              <SelectItem value="25">25</SelectItem>
                              <SelectItem value="50">50</SelectItem>
                              <SelectItem value="100">100</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="h-8 w-8 p-0">
                          <ChevronsLeft className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} className="h-8 w-8 p-0">
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm font-medium">
                          Page {currentPage} of {totalPages}
                        </span>
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} className="h-8 w-8 p-0">
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="h-8 w-8 p-0">
                          <ChevronsRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="project-overrides">
          <Card>
            <CardContent className="pt-6">
              <ProjectRoleOverridesTab />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employee-overrides">
          <Card>
            <CardContent className="pt-6">
              <EmployeeRoleOverridesTab />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
